import React, { useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";
import Drawer, { PanelSection, PanelInput } from "./Drawer";
import { roomColor } from "@/components/schedule/FilterBar";
import { roomsApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";

const COLOR_TOKENS = ["magenta", "cyan", "violet", "lime", "amber", "coral", "azure", "rose"];

const DRAG_MIME = "application/x-ps-room-reorder";

export default function RoomsPanel({ open, onClose, projectId, rooms, onMutated }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState({ name: "", abbreviation: "", color_token: "amber", notes: "" });
  const [busy, setBusy] = useState(false);

  // Drag-and-drop reorder state. `draggingId` = visual-only "ghost" state for
  // the row being picked up. The dragged id is also stored in a ref so the
  // dragover/drop handlers can read it synchronously (state updates are async,
  // and `dragover` can fire on the same tick as `dragstart` in synthetic tests).
  const [draggingId, setDraggingId] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  const draggingIdRef = React.useRef(null);
  const dropAtRef = React.useRef(null);

  const ordered = [...rooms].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const startAdd = async () => {
    if (!adding.name.trim()) return;
    setBusy(true);
    try {
      await roomsApi.create(projectId, {
        name: adding.name.trim(),
        abbreviation: adding.abbreviation.trim().toUpperCase() || undefined,
        color_token: adding.color_token,
        notes: adding.notes || undefined,
        sort_order: rooms.length,
      });
      setAdding({ name: "", abbreviation: "", color_token: "amber", notes: "" });
      onMutated && (await onMutated());
      toast.success("Room added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (room, updates) => {
    try {
      await roomsApi.update(room.id, updates);
      onMutated && (await onMutated());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (room) => {
    const ok = await confirm({
      title: `Delete room "${room.name}"?`,
      body: "Existing schedule blocks in this room will keep referencing it.",
      confirmLabel: "DELETE",
      danger: true,
    });
    if (!ok) return;
    try {
      await roomsApi.remove(room.id);
      onMutated && (await onMutated());
      toast.success("Room deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Commit a reorder: move the dragged room to position computed from `dropAt`.
  const commitReorder = async (fromId, target) => {
    if (!fromId || !target) return;
    const fromIdx = ordered.findIndex((r) => r.id === fromId);
    if (fromIdx === -1) return;
    let toIdx = target.idx + (target.side === "after" ? 1 : 0);
    // Removing the source first shifts indices for the target.
    if (toIdx > fromIdx) toIdx -= 1;
    if (toIdx === fromIdx) return; // no-op
    const next = ordered.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    try {
      await Promise.all(next.map((r, i) => (r.sort_order === i ? null : roomsApi.update(r.id, { sort_order: i }))).filter(Boolean));
      onMutated && (await onMutated());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="ROOMS" count={rooms.length} dataTestid="rooms-panel">
      <PanelSection title="LIST">
        <div
          className="space-y-2"
          data-testid="rooms-reorder-list"
          onDragOver={(e) => {
            // Allow drop at the very end of the list (after the last row).
            if (Array.from(e.dataTransfer.types).includes(DRAG_MIME) && ordered.length > 0) {
              e.preventDefault();
            }
          }}
        >
          {ordered.map((r, i) => (
            <RoomRow
              key={r.id}
              room={r}
              index={i}
              editing={editing === r.id}
              isDragging={draggingId === r.id}
              dropAt={dropAt && dropAt.idx === i ? dropAt.side : null}
              onStartEdit={() => setEditing(r.id)}
              onCancelEdit={() => setEditing(null)}
              onSave={(updates) => { saveEdit(r, updates); setEditing(null); }}
              onRemove={() => remove(r)}
              onDragStart={(e) => {
                draggingIdRef.current = r.id;
                setDraggingId(r.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData(DRAG_MIME, r.id);
              }}
              onDragEnd={() => {
                draggingIdRef.current = null;
                dropAtRef.current = null;
                setDraggingId(null);
                setDropAt(null);
              }}
              onDragOver={(e, side) => {
                if (!Array.from(e.dataTransfer.types).includes(DRAG_MIME)) return;
                if (draggingIdRef.current === r.id) return; // same row, ignore
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                const next = { idx: i, side };
                dropAtRef.current = next;
                setDropAt(next);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const fromId = e.dataTransfer.getData(DRAG_MIME) || draggingIdRef.current;
                const target = dropAtRef.current || dropAt;
                draggingIdRef.current = null;
                dropAtRef.current = null;
                setDraggingId(null);
                setDropAt(null);
                commitReorder(fromId, target);
              }}
            />
          ))}
        </div>
        {ordered.length > 1 && (
          <div className="mt-1 mono text-2xs uppercase tracking-widest text-ink-mute opacity-60">
            ⇕ DRAG ROW HANDLE TO REORDER
          </div>
        )}
      </PanelSection>

      <PanelSection title="+ NEW ROOM">
        <PanelInput label="NAME" value={adding.name} onChange={(v) => setAdding({ ...adding, name: v })} placeholder="Stage D" dataTestid="room-name-input" />
        <PanelInput label="ABBREVIATION (3 chars)" value={adding.abbreviation} onChange={(v) => setAdding({ ...adding, abbreviation: v.slice(0, 3) })} placeholder="STD" />
        <div className="mb-2">
          <span className="mono text-2xs uppercase tracking-widest text-ink-mute block mb-1">COLOR</span>
          <div className="flex gap-1 flex-wrap">
            {COLOR_TOKENS.map((c) => (
              <button
                key={c}
                onClick={() => setAdding({ ...adding, color_token: c })}
                className={`w-6 h-6 border-2 ${adding.color_token === c ? "border-amber" : "border-line"}`}
                style={{ background: roomColor(c) }}
                title={c}
                data-testid={`color-token-${c}`}
              />
            ))}
          </div>
        </div>
        <button onClick={startAdd} disabled={busy || !adding.name.trim()} className="btn-primary w-full justify-center" data-testid="room-add-btn">
          <Plus className="w-3 h-3" /> ADD ROOM
        </button>
      </PanelSection>
    </Drawer>
  );
}

function RoomRow({
  room, index, editing, isDragging, dropAt,
  onStartEdit, onCancelEdit, onSave, onRemove,
  onDragStart, onDragEnd, onDragOver, onDrop,
}) {
  const [draft, setDraft] = useState({ name: room.name, abbreviation: room.abbreviation, color_token: room.color_token, notes: room.notes || "" });
  React.useEffect(() => {
    if (editing) setDraft({ name: room.name, abbreviation: room.abbreviation, color_token: room.color_token, notes: room.notes || "" });
  }, [editing, room]);

  if (editing) {
    return (
      <div className="p-2 border border-amber bg-bg-elevated space-y-2" data-testid={`room-row-${room.id}`}>
        <PanelInput label="NAME" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} />
        <PanelInput label="ABBR" value={draft.abbreviation} onChange={(v) => setDraft({ ...draft, abbreviation: v.slice(0, 3) })} />
        <div>
          <span className="mono text-2xs uppercase tracking-widest text-ink-mute block mb-1">COLOR</span>
          <div className="flex gap-1 flex-wrap">
            {COLOR_TOKENS.map((c) => (
              <button
                key={c}
                onClick={() => setDraft({ ...draft, color_token: c })}
                className={`w-6 h-6 border-2 ${draft.color_token === c ? "border-amber" : "border-line"}`}
                style={{ background: roomColor(c) }}
              />
            ))}
          </div>
        </div>
        <PanelInput label="NOTES" value={draft.notes} onChange={(v) => setDraft({ ...draft, notes: v })} />
        <div className="flex gap-1">
          <button onClick={() => onSave(draft)} className="btn-primary flex-1 justify-center">SAVE</button>
          <button onClick={onCancelEdit} className="btn-ghost">CANCEL</button>
        </div>
      </div>
    );
  }

  // Drop indicator: 2px amber bar above or below the row, based on cursor side.
  // The dragover handler is split into two zones (top half = before, bottom half = after).
  const handleDragOver = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const side = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
    onDragOver && onDragOver(e, side);
  };

  return (
    <div
      data-testid={`room-row-${room.id}`}
      className={clsx(
        "relative flex items-center gap-2 p-2 border bg-bg-elevated group transition-colors",
        isDragging ? "opacity-40 border-amber border-dashed" : "border-line hover:border-line-strong"
      )}
      onDragOver={handleDragOver}
      onDrop={onDrop}
    >
      {dropAt === "before" && (
        <div className="absolute -top-[3px] left-0 right-0 h-[2px] bg-amber pointer-events-none" data-testid={`room-drop-before-${room.id}`} />
      )}
      {dropAt === "after" && (
        <div className="absolute -bottom-[3px] left-0 right-0 h-[2px] bg-amber pointer-events-none" data-testid={`room-drop-after-${room.id}`} />
      )}

      {/* Drag handle — the ONLY draggable element. Keeps clicks on the row
          body / edit / delete from initiating a drag. */}
      <span
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="shrink-0 cursor-grab active:cursor-grabbing text-ink-mute hover:text-amber opacity-50 group-hover:opacity-100 transition-opacity"
        title="Drag to reorder"
        data-testid={`room-drag-handle-${room.id}`}
        aria-label={`Drag handle for ${room.name}`}
      >
        <GripVertical className="w-3 h-3" />
      </span>

      <span className="w-1 self-stretch" style={{ background: roomColor(room.color_token) }} />
      <span className="mono text-2xs uppercase tracking-wider text-amber shrink-0 w-8">{room.abbreviation}</span>
      <span className="text-xs text-ink flex-1 truncate">{room.name}</span>
      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
        <button onClick={onStartEdit} className="btn-ghost" data-testid={`room-edit-${room.id}`}>EDIT</button>
        <button onClick={onRemove} className="text-ink-mute hover:text-red" data-testid={`room-delete-${room.id}`}><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}
